// Pille mit Schloss und Paketname für gesperrte Funktionen. Bewusst neutral
// grau statt aivyOrange, der Dialog verkauft, die Pille markiert nur.
//
// LockBadge ist ein Button und öffnet den Upgrade-Dialog des globalen Hosts.
// LockPill ist dieselbe Optik ohne Aktion, für Stellen, die selbst schon
// klickbar sind oder in einem offenen Modal liegen (UpgradeInline,
// Overlay von LockedSection).
import { LockClosedIcon } from '@heroicons/react/16/solid'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { FEATURES } from '../../features/pricing/plans'
import {
  describeFeatureLock,
  useFeatureGate
} from '../../features/pricing/use-feature-gate'
import { classNames } from '../../helpers'

const PILL =
  'inline-flex shrink-0 items-center rounded-full bg-gray-100 font-medium text-gray-700 ring-1 ring-inset ring-gray-200'

const SIZES = {
  xs: { pill: 'gap-x-1 px-2 py-0.5 text-xs', icon: 'size-3.5' },
  sm: { pill: 'gap-x-1.5 px-2.5 py-1 text-sm', icon: 'size-4' }
}

/** Titel eines Features aus pricing.features, ohne Key leer */
export const featureTitle = (t, feature) => {
  const i18nKey = FEATURES[feature]?.i18nKey
  return i18nKey ? t(`${i18nKey}.title`) : ''
}

/**
 * Texte zur Pille eines Features, siehe describeFeatureLock. ariaLabel
 * beschreibt die Sperre für Screenreader.
 */
export const useLockTexts = (feature, { source, inUse = false } = {}) => {
  const { t } = useTranslation()
  const gate = useFeatureGate(feature, { source, inUse })
  const texts = describeFeatureLock({ t, ...gate })
  const title = featureTitle(t, feature)

  return {
    ...texts,
    gate,
    title,
    ariaLabel: title
      ? t('pricing.lock.aria', { feature: title })
      : t('pricing.lock.tooltip')
  }
}

/**
 * Optik der Pille ohne Aktion. wrap erlaubt Umbrüche für lange Sätze wie
 * „In Growth und Scale zubuchbar, enthalten in Unlimited".
 */
export const LockPill = ({ label, size, wrap, className }) => {
  const styles = SIZES[size] || SIZES.xs

  return (
    <span
      className={classNames(
        PILL,
        styles.pill,
        wrap ? 'whitespace-normal' : 'whitespace-nowrap',
        className
      )}
    >
      <LockClosedIcon
        aria-hidden='true'
        className={classNames(styles.icon, 'text-gray-500')}
      />
      {label}
    </span>
  )
}

LockPill.propTypes = {
  label: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['xs', 'sm']),
  wrap: PropTypes.bool,
  className: PropTypes.string
}

LockPill.defaultProps = {
  size: 'xs',
  wrap: false,
  className: undefined
}

/**
 * Schloss-Pille als Button. Der Klick stoppt die Propagation, weil die Pille
 * oft in klickbaren Zeilen sitzt, und öffnet ohne onClick den Upgrade-Dialog.
 * iconOnly ist eine runde Fläche mit 24 px für Tabellen und Toggle-Zeilen.
 * Der Tooltip läuft nur über title, mobil öffnet ein Tipp direkt den Dialog.
 *
 * Die Pille entscheidet nicht selbst, ob sie erscheint. Das macht der
 * Aufrufer, in der Regel über useFeatureGate (locked nur in enforced).
 */
const LockBadge = ({
  feature,
  source,
  size,
  iconOnly,
  label,
  inUse,
  onClick,
  className
}) => {
  const {
    gate,
    label: lockLabel,
    includedText,
    ariaLabel
  } = useLockTexts(feature, { source, inUse })
  const text = label || lockLabel
  const styles = SIZES[size] || SIZES.xs

  const handleClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (onClick) {
      onClick(event)
      return
    }
    // Über das Gate, damit source und inUse im Dialog ankommen
    gate.open()
  }

  return (
    <button
      type='button'
      aria-haspopup='dialog'
      aria-label={iconOnly ? ariaLabel : `${text}, ${ariaLabel}`}
      title={includedText}
      data-feature={feature}
      onClick={handleClick}
      className={classNames(
        PILL,
        'justify-center whitespace-nowrap hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue',
        iconOnly ? 'size-6' : styles.pill,
        className
      )}
    >
      <LockClosedIcon
        aria-hidden='true'
        className={classNames(
          iconOnly ? 'size-3.5' : styles.icon,
          'text-gray-500'
        )}
      />
      {!iconOnly && text}
    </button>
  )
}

LockBadge.propTypes = {
  feature: PropTypes.oneOf(Object.keys(FEATURES)).isRequired,
  source: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm']),
  iconOnly: PropTypes.bool,
  label: PropTypes.string,
  inUse: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string
}

LockBadge.defaultProps = {
  source: undefined,
  size: 'xs',
  iconOnly: false,
  label: undefined,
  inUse: false,
  onClick: undefined,
  className: undefined
}

export default LockBadge
